import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CostCentreValidationIndexEntry,
  CostCheckState,
  CostLineIndexEntry,
  DictionaryValidationFeedsStatus,
  DictionaryValidationIndexEntry,
  DictionaryValidationListOptions,
  ProductTypeValidationIndexEntry,
} from "@shared-core/data/cost-engine-validation-types";
import {
  costCheckStateLabel,
  costCheckStateTone,
  formatCostLineDifferenceTotal,
  formatCostLineLargestDelta,
  productTypeLabel,
  type RollingValidationView,
} from "@shared-core/domain/cost-engine-validation-presentation";
import { defaultLiveCostingChecksView } from "@shared-core/domain/live-costing-checks-default-view";
import { siteTownName } from "@shared-core/domain/manufacturing-setup-presentation";
import { ComparisonGroupHeaderCell } from "../app/components/patterns/ComparisonTriplet";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../app/components/primitives/badge";
import { Input } from "../app/components/primitives/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Tabs, TabsList, TabsTrigger } from "../app/components/primitives/tabs";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { CostCheckCell } from "../app/components/domain/catalogue/CostCheckBadges";
import {
  FeedsBanner,
  FeedsBannerError,
} from "../app/components/domain/catalogue/FeedsBanner";
import { Search } from "lucide-react";

const PAGE_SIZE = 25;

const VIEWS: { value: RollingValidationView; label: string }[] = [
  { value: "item", label: "By item" },
  { value: "cost-line", label: "By cost line" },
  { value: "cost-centre", label: "By cost centre" },
  { value: "product-type", label: "By product type" },
];

/**
 * The Live costing › Checks tab (design decision, 12 Sep,
 * `03_manufacturing_surfaces_design.md` "setup visibility and costing
 * validation are separate"): the one home for investigating the check
 * ledger, in four roll-up views. "By item" and "by cost line" moved here
 * from the dictionary index; "by cost centre" is the never-checked routing
 * rule's own data; "by product type" is coverage. Curating rolling-set
 * membership itself (pin/unpin one specific price) happens on the item's
 * Live costing matrix, through Check now's "Also check weekly" box, not here — there is no
 * coarser unit than a price, so no separate flat "set" list is needed.
 *
 * Deep-linkable per view and per cost-centre/product-type filter (query
 * params) so the Manufacturing setup detail page's "see live costing
 * checks" link, and the Routing policy page's excluded-presses list, land
 * on the right slice. With no explicit `?view=`, the tab opens on **by cost
 * line** when any cost line is in Mismatch — the view a print manager needs
 * to act on — otherwise **by item** (`defaultLiveCostingChecksView`).
 */
export function LiveCostingChecksPage() {
  const adapter = useDataAdapter();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = searchParams.get("view");
  const explicitView = VIEWS.some((v) => v.value === rawView)
    ? (rawView as RollingValidationView)
    : null;

  const [feedsStatus, setFeedsStatus] =
    useState<DictionaryValidationFeedsStatus | null>(null);
  const [feedsError, setFeedsError] = useState(false);
  const [costLines, setCostLines] = useState<CostLineIndexEntry[] | null>(null);
  const [costLinesError, setCostLinesError] = useState(false);

  useEffect(() => {
    adapter
      .getDictionaryValidationFeedsStatus()
      .then(setFeedsStatus)
      .catch(() => setFeedsError(true));
  }, [adapter]);

  useEffect(() => {
    adapter
      .listValidationCostLines()
      .then((res) => setCostLines(res.items))
      .catch(() => setCostLinesError(true));
  }, [adapter]);

  // With no explicit `?view=`, the default view depends on the cost-line
  // health data, which loads async. Hold off resolving a default until that
  // data is in (loaded or errored) — otherwise the board briefly selects
  // "By item" (the empty-data default) and then flips to "By cost line" once
  // the mismatch is known, and a tab click made during that window is
  // swallowed as a no-op because the target tab already looks selected.
  const costLinesLoaded = costLines !== null || costLinesError;
  const view: RollingValidationView | null =
    explicitView ??
    (costLinesLoaded ? defaultLiveCostingChecksView(costLines ?? []) : null);

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        The check ledger, from four angles — by item, by cost line, by cost
        centre (the "never route to a press not yet used in any price check"
        rule's own data) and by product type. Each row links through to its item
        or cost-line page.
      </p>

      {feedsError ? (
        <FeedsBannerError />
      ) : (
        feedsStatus && <FeedsBanner status={feedsStatus} />
      )}

      <Tabs
        value={view ?? ""}
        onValueChange={(v) =>
          setSearchParams(
            (prev) => {
              const params = new URLSearchParams(prev);
              params.set("view", v);
              return params;
            },
            { replace: true },
          )
        }
      >
        <TabsList>
          {VIEWS.map((v) => (
            <TabsTrigger key={v.value} value={v.value}>
              {v.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {view === null && (
        <div className="border rounded-lg">
          <p className="py-8 text-center text-muted-foreground">Loading…</p>
        </div>
      )}
      {view === "item" && <ByItemView />}
      {view === "cost-line" && (
        <ByCostLineView costLines={costLines} loadError={costLinesError} />
      )}
      {view === "cost-centre" && (
        <ByCostCentreView
          initialCostCentreId={searchParams.get("costCentreId")}
        />
      )}
      {view === "product-type" && (
        <ByProductTypeView
          initialProductTypeId={searchParams.get("productTypeId")}
        />
      )}
    </div>
  );
}

// ── "By dictionary item" view (moved from DictionaryAdminPage) ───────────

function ByItemView() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const sort = searchParams.get("sort") ?? "name";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const costCheckFilter = (searchParams.get("costCheck") ?? "") as
    CostCheckState | "";

  const [items, setItems] = useState<DictionaryValidationIndexEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);

  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null) params.delete(key);
            else params.set(key, value);
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPage = (p: number) =>
    updateParams({ page: p === 1 ? null : String(p) });

  const load = useCallback(() => {
    const opts: DictionaryValidationListOptions = {
      page,
      pageSize: PAGE_SIZE,
      sort,
    };
    if (debouncedQ) opts.q = debouncedQ;
    if (costCheckFilter) opts.costCheckState = costCheckFilter;
    setLoadError(false);
    adapter
      .listDictionaryValidationItems(opts)
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
        setTotalPages(res.totalPages);
        setFirstLoad(false);
      })
      .catch(() => {
        setLoadError(true);
        setFirstLoad(false);
      });
  }, [adapter, page, sort, debouncedQ, costCheckFilter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1 min-w-[12rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name..."
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              updateParams({ page: null });
            }}
            className="pl-9"
          />
        </div>
        <select
          aria-label="Filter by cost check"
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          value={costCheckFilter}
          onChange={(e) =>
            updateParams({ costCheck: e.target.value || null, page: null })
          }
        >
          <option value="">All cost checks</option>
          <option value="green">Matches</option>
          <option value="amber">Small differences</option>
          <option value="red">Mismatch</option>
          <option value="grey">Not yet checked</option>
        </select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Product type</TableHead>
              <TableHead>Cost check</TableHead>
              <TableHead>Penny-exact / within 5%</TableHead>
              <TableHead>Last checked</TableHead>
              <TableHead>Rolling checks (4 weeks)</TableHead>
              <TableHead>Used by (catalogue items)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {firstLoad ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : loadError ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-destructive"
                >
                  Couldn't load the dictionary. Please try again.
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  No dictionary items match these filters
                </TableCell>
              </TableRow>
            ) : (
              items.map((entry) => (
                <TableRow
                  key={entry.dictionaryItemId}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(
                      paths.dictionaryItemRecord({
                        dictionaryItemId: entry.dictionaryItemId,
                      }),
                    )
                  }
                >
                  <TableCell className="font-medium">{entry.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {productTypeLabel(
                      entry.productTypeId,
                      entry.productTypeName,
                    )}
                  </TableCell>
                  <TableCell>
                    <CostCheckCell
                      state={entry.costCheckState}
                      mismatchingCostLine={entry.mismatchingCostLine}
                      lastCheck={entry.lastCheck}
                    />
                  </TableCell>
                  <TableCell className="text-muted-foreground tabular-nums">
                    {entry.pennyExactPct != null
                      ? `${entry.pennyExactPct}% / ${entry.within5PctPct}%`
                      : "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTimeOrDash(entry.lastCheckedAt)}
                  </TableCell>
                  <TableCell
                    className={
                      entry.rollingChecksCount === 0
                        ? "text-destructive font-medium tabular-nums"
                        : "text-muted-foreground tabular-nums"
                    }
                  >
                    {entry.rollingChecksCount}
                    {entry.quarantinedPriceCount > 0 && (
                      <span className="ml-1 text-xs font-medium text-destructive">
                        · {entry.quarantinedPriceCount} quarantined
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {entry.usedByCount}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      {!firstLoad && !loadError && (
        <p className="text-xs text-muted-foreground">
          {total} {total === 1 ? "item" : "items"}
        </p>
      )}
    </div>
  );
}

// ── "By cost line" view (moved from DictionaryAdminPage) ─────────────────
// The drill-in is a full page (`settingsLiveCostingCostLine`), not a side
// sheet (review decision, 02_glossary_and_live_costing.md) — the board
// below just links each row there.

// Cost lines are fetched once by the page (they also decide the tab's
// default view) and passed down here, rather than fetched again per view.
function ByCostLineView({
  costLines,
  loadError,
}: {
  costLines: CostLineIndexEntry[] | null;
  loadError: boolean;
}) {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cost line</TableHead>
              <TableHead>Cost check</TableHead>
              <TableHead>Product types affected</TableHead>
              <TableHead>Checks</TableHead>
              <TableHead>Differing checks</TableHead>
              <TableHead className="text-right">Difference (total)</TableHead>
              <TableHead className="text-right">Largest over</TableHead>
              <TableHead className="text-right">Largest under</TableHead>
              <TableHead>Last checked</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadError ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-8 text-destructive"
                >
                  Couldn't load the by-cost-line view. Please try again.
                </TableCell>
              </TableRow>
            ) : costLines === null ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : costLines.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-8 text-muted-foreground"
                >
                  No cost lines in the check ledger yet.
                </TableCell>
              </TableRow>
            ) : (
              costLines.map((c) => (
                <TableRow
                  key={`${c.costLine.costCentreId}:${c.costLine.component}`}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(
                      paths.settingsLiveCostingCostLine({
                        costCentreId: c.costLine.costCentreId,
                        component: c.costLine.component,
                      }),
                    )
                  }
                >
                  <TableCell className="font-medium">
                    {c.costLine.label}
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      {c.costLine.costCentreCode}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={costCheckStateTone(c.health)}>
                      {costCheckStateLabel(c.health)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    PT{c.productTypesAffected.join(", PT")}
                  </TableCell>
                  <TableCell className="tabular-nums">{c.checkCount}</TableCell>
                  <TableCell className="tabular-nums">{c.missCount}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.moneySummary.differingCheckCount === 0
                      ? "—"
                      : formatCostLineDifferenceTotal(
                          c.moneySummary.differenceTotalPence,
                          c.moneySummary.differingCheckCount,
                        )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.moneySummary.largestOverPence == null
                      ? "—"
                      : formatCostLineLargestDelta(
                          c.moneySummary.largestOverPence,
                          c.moneySummary.largestOverCount,
                        )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.moneySummary.largestUnderPence == null
                      ? "—"
                      : formatCostLineLargestDelta(
                          c.moneySummary.largestUnderPence,
                          c.moneySummary.largestUnderCount,
                        )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTimeOrDash(c.lastCheckedAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── "By cost centre" view — the never-checked routing rule's own data ────

function ByCostCentreView({
  initialCostCentreId,
}: {
  initialCostCentreId: string | null;
}) {
  const adapter = useDataAdapter();
  const [items, setItems] = useState<CostCentreValidationIndexEntry[] | null>(
    null,
  );
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    adapter
      .listValidationByCostCentre()
      .then((res) => setItems(res.items))
      .catch(() => setLoadError(true));
  }, [adapter]);

  const highlightId = initialCostCentreId ? Number(initialCostCentreId) : null;

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead rowSpan={2} className="align-bottom">
                Cost centre
              </TableHead>
              <TableHead rowSpan={2} className="align-bottom">
                Site
              </TableHead>
              <TableHead rowSpan={2} className="align-bottom">
                Price checks used in
              </TableHead>
              <TableHead rowSpan={2} className="align-bottom">
                Last checked
              </TableHead>
              <ComparisonGroupHeaderCell label="Cost lines" colSpan={3} />
            </TableRow>
            <TableRow>
              <TableHead className="border-l border-border">Matches</TableHead>
              <TableHead>Small differences</TableHead>
              <TableHead>Mismatch</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadError ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-destructive"
                >
                  Couldn't load the by-cost-centre view. Please try again.
                </TableCell>
              </TableRow>
            ) : items === null ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  No cost centres in the check ledger yet.
                </TableCell>
              </TableRow>
            ) : (
              items.map((c) => (
                <TableRow
                  key={c.costCentreId}
                  className={
                    highlightId === c.costCentreId ? "bg-muted/50" : undefined
                  }
                >
                  <TableCell className="font-medium">
                    {c.costCentreId} {c.costCentreCode}
                    <div className="text-xs font-normal text-muted-foreground">
                      {c.costCentreName}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {siteTownName(c.site)}
                  </TableCell>
                  <TableCell
                    className={
                      c.priceChecksUsedIn === 0
                        ? "text-destructive font-medium tabular-nums"
                        : "tabular-nums"
                    }
                  >
                    {c.priceChecksUsedIn === 0
                      ? "Not yet used in any price check"
                      : c.priceChecksUsedIn}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTimeOrDash(c.lastCheckedAt)}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {c.matchesCount}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {c.smallDifferencesCount}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {c.mismatchCount}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── "By product type" view — coverage roll-up ─────────────────────────────

function ByProductTypeView({
  initialProductTypeId,
}: {
  initialProductTypeId: string | null;
}) {
  const adapter = useDataAdapter();
  const [items, setItems] = useState<ProductTypeValidationIndexEntry[] | null>(
    null,
  );
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    adapter
      .listValidationByProductType()
      .then((res) => setItems(res.items))
      .catch(() => setLoadError(true));
  }, [adapter]);

  const highlightId = initialProductTypeId
    ? Number(initialProductTypeId)
    : null;

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead rowSpan={2} className="align-bottom">
                Product type
              </TableHead>
              <TableHead rowSpan={2} className="align-bottom">
                Items
              </TableHead>
              <TableHead rowSpan={2} className="align-bottom">
                Prices checked in window
              </TableHead>
              <ComparisonGroupHeaderCell label="Prices" colSpan={4} />
              <TableHead
                rowSpan={2}
                className="align-bottom border-l border-border"
              >
                Last checked
              </TableHead>
            </TableRow>
            <TableRow>
              <TableHead className="border-l border-border">Matches</TableHead>
              <TableHead>Small differences</TableHead>
              <TableHead>Mismatch</TableHead>
              <TableHead>Not yet checked</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadError ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-destructive"
                >
                  Couldn't load the by-product-type view. Please try again.
                </TableCell>
              </TableRow>
            ) : items === null ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-muted-foreground"
                >
                  No product types in the check ledger yet.
                </TableCell>
              </TableRow>
            ) : (
              items.map((pt) => (
                <TableRow
                  key={pt.productTypeId}
                  className={
                    highlightId === pt.productTypeId ? "bg-muted/50" : undefined
                  }
                >
                  <TableCell className="font-medium">
                    {productTypeLabel(pt.productTypeId, pt.productTypeName)}
                  </TableCell>
                  <TableCell className="tabular-nums">{pt.itemCount}</TableCell>
                  <TableCell className="tabular-nums">
                    {pt.pricesCheckedInWindow}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {pt.matchesCount}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {pt.smallDifferencesCount}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {pt.mismatchCount}
                  </TableCell>
                  <TableCell
                    className={
                      pt.notYetCheckedCount > 0
                        ? "text-destructive font-medium tabular-nums"
                        : "tabular-nums"
                    }
                  >
                    {pt.notYetCheckedCount}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDateTimeOrDash(pt.lastCheckedAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
